// frontend/components/AdminChat.js - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  Send,
  Search,
  User,
  CheckCircle,
  Loader,
  AlertCircle,
  BarChart3,
  Filter,
  Users,
  RefreshCw,
  Zap,
  Bell,
  Wifi,
  WifiOff,
  Mail,
  Download,
  Copy,
  FileText,
  Clock,
  Trash2,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// 🔧 CUSTOM HOOK: Polling Manager
const usePollingManager = () => {
  const intervalsRef = useRef(new Map());
  const timeoutsRef = useRef(new Map());
  
  const setPollingInterval = useCallback((key, callback, interval) => {
    clearPollingInterval(key);
    const id = setInterval(callback, interval);
    intervalsRef.current.set(key, id);
    return id;
  }, []);
  
  const clearPollingInterval = useCallback((key) => {
    if (intervalsRef.current.has(key)) {
      clearInterval(intervalsRef.current.get(key));
      intervalsRef.current.delete(key);
    }
  }, []);
  
  const setPollingTimeout = useCallback((key, callback, delay) => {
    clearPollingTimeout(key);
    const id = setTimeout(callback, delay);
    timeoutsRef.current.set(key, id);
    return id;
  }, []);
  
  const clearPollingTimeout = useCallback((key) => {
    if (timeoutsRef.current.has(key)) {
      clearTimeout(timeoutsRef.current.get(key));
      timeoutsRef.current.delete(key);
    }
  }, []);
  
  const clearAllPolling = useCallback(() => {
    intervalsRef.current.forEach((id) => clearInterval(id));
    timeoutsRef.current.forEach((id) => clearTimeout(id));
    intervalsRef.current.clear();
    timeoutsRef.current.clear();
  }, []);
  
  return {
    setPollingInterval,
    clearPollingInterval,
    setPollingTimeout,
    clearPollingTimeout,
    clearAllPolling
  };
};

export default function AdminChat() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isConnected, setIsConnected] = useState(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportRecipientEmail, setExportRecipientEmail] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [lastPollTime, setLastPollTime] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const seenMessageIds = useRef(new Set());
  const isMounted = useRef(true);
  const lastFetchTimeRef = useRef({});
  
  // 🔧 Use custom polling manager
  const {
    setPollingInterval,
    clearPollingInterval,
    clearAllPolling
  } = usePollingManager();

  // ==================== LIFECYCLE ====================

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearAllPolling();
    };
  }, [clearAllPolling]);

  // ==================== HEARTBEAT & CONNECTION ====================

  const sendHeartbeat = useCallback(async () => {
    if (!isMounted.current || !user?.role || !(user.role === 'admin' || user.role === 'support_agent')) return;
    
    try {
      await api.sendChatHeartbeat();
    } catch (err) {
      console.warn('Heartbeat failed:', err.message);
    }
  }, [user]);

  const checkConnection = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      const result = await api.ping();
      if (result.success) {
        if (!isConnected) {
          setIsConnected(true);
          loadAllData();
        }
      } else {
        setIsConnected(false);
      }
    } catch (err) {
      setIsConnected(false);
    }
  }, [isConnected]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'support_agent') {
      sendHeartbeat();
      const heartbeatInterval = setInterval(sendHeartbeat, 30000);
      const connectionInterval = setInterval(checkConnection, 45000);
      
      return () => {
        clearInterval(heartbeatInterval);
        clearInterval(connectionInterval);
      };
    }
  }, [user, sendHeartbeat, checkConnection]);

  // ==================== POLLING MANAGEMENT ====================

  useEffect(() => {
    if (autoRefresh && (user?.role === 'admin' || user?.role === 'support_agent')) {
      // Inbox polling every 10 seconds (reduced from 5s)
      setPollingInterval('inbox', loadInbox, 10000);
      
      if (selectedThread) {
        // Thread polling every 8 seconds
        setPollingInterval('thread', refreshSelectedThread, 8000);
      }
    } else {
      clearPollingInterval('inbox');
      clearPollingInterval('thread');
    }
    
    return () => {
      if (!autoRefresh) {
        clearPollingInterval('inbox');
        clearPollingInterval('thread');
      }
    };
  }, [autoRefresh, user, selectedThread, setPollingInterval, clearPollingInterval]);

  // ==================== DATA LOADING ====================

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'support_agent') {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadInbox(), loadStats()]);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInbox = async () => {
    if (!isMounted.current) return;
    
    try {
      const result = await api.getChatAdminInbox();
      if (result.success) {
        const newInbox = result.data.inbox || [];
        setInbox(newInbox);
        setThreads(newInbox);
        setNotificationCount(result.data.totalUnread || 0);
        setLastUpdateTime(new Date());
        
        if (selectedThread) {
          const updatedThread = newInbox.find(t => t.thread_id === selectedThread.thread_id);
          if (updatedThread) {
            setSelectedThread(prev => ({
              ...prev,
              ...updatedThread
            }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load inbox:', err);
      if (err.message?.includes('network') || err.message?.includes('Failed to fetch')) {
        setIsConnected(false);
      }
    }
  };

  const loadStats = async () => {
    try {
      const result = await api.getChatStats();
      if (result.success) {
        setStats(result.data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const refreshSelectedThread = async () => {
    if (!selectedThread || !isMounted.current) return;
    
    try {
      const now = Date.now();
      const lastFetch = lastFetchTimeRef.current[selectedThread.thread_id] || 0;
      
      // Rate limit: only fetch if 8+ seconds have passed
      if (now - lastFetch < 8000) return;
      
      lastFetchTimeRef.current[selectedThread.thread_id] = now;
      
      const lastMsgId = lastMessageIdRef.current;
      const result = await api.getChatNewMessages(selectedThread.thread_id, lastMsgId);
      
      if (result.success && result.data.messages?.length > 0) {
        const newMessages = result.data.messages;
        
        const uniqueNewMessages = newMessages.filter(msg => {
          if (seenMessageIds.current.has(msg.message_id)) {
            return false;
          }
          seenMessageIds.current.add(msg.message_id);
          return true;
        });
        
        if (uniqueNewMessages.length > 0) {
          setMessages(prev => {
            const combined = [...prev, ...uniqueNewMessages];
            return combined.sort((a, b) => 
              new Date(a.created_at) - new Date(b.created_at)
            );
          });
          
          lastMessageIdRef.current = uniqueNewMessages[uniqueNewMessages.length - 1].message_id;
          
          setTimeout(() => {
            if (messagesEndRef.current && isMounted.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
      }
      
      setLastPollTime(new Date());
      
    } catch (err) {
      console.error('Error refreshing thread:', err);
    }
  };

  const loadThreadMessages = async (thread) => {
    try {
      setLoading(true);
      setError('');
      setShowExportMenu(false);
      
      clearPollingInterval('thread');
      seenMessageIds.current.clear();
      lastMessageIdRef.current = null;
      lastFetchTimeRef.current[thread.thread_id] = Date.now();

      const result = await api.getChatThread(thread.thread_id);
      if (result.success) {
        const threadData = result.data.thread;
        const threadMessages = threadData.messages || [];
        
        threadMessages.forEach(msg => {
          seenMessageIds.current.add(msg.message_id);
        });
        
        if (threadMessages.length > 0) {
          lastMessageIdRef.current = threadMessages[threadMessages.length - 1].message_id;
        }
        
        const updatedThread = {
          ...threadData,
          participantName: threadData.guest_name || threadData.participantName || 'User',
          participantEmail: threadData.guest_email || threadData.participantEmail || '',
          subject: threadData.subject || 'No subject',
          status: threadData.status || 'active',
        };
        
        setSelectedThread(updatedThread);
        setMessages(threadMessages);
        
        setInbox(prev => prev.map(t => 
          t.thread_id === thread.thread_id ? updatedThread : t
        ));
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // ==================== MESSAGE HANDLING ====================

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedThread) return;

    try {
      setLoading(true);
      setError('');

      const clientMessageId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const result = await api.sendChatMessage(selectedThread.thread_id, {
        content: inputMessage,
        clientMessageId
      });

      if (result.success && result.data.message && !result.data.duplicate) {
        const sentMessage = result.data.message;
        
        seenMessageIds.current.add(sentMessage.message_id);
        
        setMessages(prev => {
          const newMessages = [...prev, sentMessage];
          return newMessages.sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          );
        });
        
        lastMessageIdRef.current = sentMessage.message_id;
        setInputMessage('');
        loadInbox();
        
        setTimeout(() => {
          if (messagesEndRef.current && isMounted.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Send message error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedThread) return;

    try {
      const result = await api.updateChatThreadStatus(selectedThread.thread_id, status);
      if (result.success) {
        const updatedThread = { ...selectedThread, status };
        setSelectedThread(updatedThread);
        
        setInbox(prev => prev.map(t => 
          t.thread_id === selectedThread.thread_id ? updatedThread : t
        ));
        
        loadStats();
      }
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) return;

    try {
      setLoading(true);
      const result = await api.searchChatThreads(searchQuery);
      if (result.success) {
        setInbox(result.data.results || []);
        setLastUpdateTime(new Date());
      }
    } catch (err) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    loadAllData();
    if (selectedThread) {
      loadThreadMessages(selectedThread);
    }
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExportText = async () => {
    if (!selectedThread) return;
    
    try {
      setExportLoading(true);
      const result = await api.exportChatTranscript(selectedThread.thread_id);
      
      if (result.success) {
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 3000);
      } else {
        setError('Failed to export chat transcript');
      }
    } catch (err) {
      console.error('Failed to export chat:', err);
      setError('Failed to export chat transcript');
    } finally {
      setExportLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!selectedThread || !messages) return;
    
    try {
      let text = `Chat Transcript\n\n`;
      text += `Thread: ${selectedThread.thread_id}\n`;
      text += `Participant: ${selectedThread.participantName}\n`;
      text += `Email: ${selectedThread.participantEmail}\n`;
      text += `Status: ${selectedThread.status}\n`;
      text += `Created: ${new Date(selectedThread.created_at).toLocaleString()}\n\n`;
      
      messages.forEach((msg, index) => {
        const time = new Date(msg.created_at).toLocaleTimeString();
        text += `${index + 1}. [${time}] ${msg.sender_name}: ${msg.content}\n`;
      });
      
      await navigator.clipboard.writeText(text);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const handleEmailTranscript = async () => {
    if (!selectedThread || !exportRecipientEmail) {
      setError('Please enter recipient email');
      return;
    }
    
    try {
      setExportLoading(true);
      
      const result = await api.emailChatTranscript(selectedThread.thread_id, {
        recipientEmail: exportRecipientEmail,
        customMessage: exportMessage
      });
      
      if (result.success) {
        setExportSuccess(true);
        setExportRecipientEmail('');
        setExportMessage('');
        setShowExportMenu(false);
        
        setTimeout(() => {
          setExportSuccess(false);
        }, 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message || 'Failed to send email');
    } finally {
      setExportLoading(false);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();

      if (date.toDateString() === today.toDateString()) {
        return formatTime(timestamp);
      }

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }

      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return '';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { 
        text: 'Active', 
        class: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        icon: '🟢'
      },
      resolved: { 
        text: 'Resolved', 
        class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        icon: '✅'
      },
      closed: { 
        text: 'Closed', 
        class: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        icon: '🔒'
      },
      ended_by_user: { 
        text: 'Ended', 
        class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        icon: '⏹️'
      }
    };
    
    return badges[status] || badges.closed;
  };

  const getMessageAlignment = (message) => {
    if (!message) return { align: 'left', isStaff: false };
    
    const actualRole = message.sender_role || message.sender_type || 'user';
    const isStaff = actualRole === 'admin' || actualRole === 'support_agent';
    const isAdmin = actualRole === 'admin';
    const isSupport = actualRole === 'support_agent' || 
                    (message.sender_name?.includes('Support') || 
                      message.sender_name?.includes('(Support Agent)'));
    
    return {
      align: isStaff ? 'right' : 'left',
      isStaff,
      isAdmin,
      isSupport,
      actualRole
    };
  };

  const getSenderName = (message) => {
    if (!message) return '';
    
    const alignment = getMessageAlignment(message);
    
    if (alignment.isSupport) {
      const name = message.sender_name || 'Support Agent';
      return name.replace(' (Support Agent)', '') + ' 🛠️';
    }
    
    if (alignment.isAdmin && !alignment.isSupport) {
      return `${message.sender_name || 'Admin'} 👑`;
    }
    
    if (alignment.actualRole === 'guest') {
      return message.sender_name || 'Guest';
    }
    
    return message.sender_name || 'User';
  };

  const filteredInbox = inbox.filter((thread) =>
    statusFilter === 'all' ? true : thread.status === statusFilter
  );

  // ==================== ACCESS CONTROL ====================

  if (user?.role !== 'admin' && user?.role !== 'support_agent') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Admin privileges required to access this dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* HEADER */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Chat Dashboard
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage customer conversations
                  {lastUpdateTime && (
                    <span className="ml-2 text-xs text-gray-500">
                      • Updated {formatTime(lastUpdateTime)}
                    </span>
                  )}
                  {lastPollTime && autoRefresh && (
                    <span className="ml-2 text-xs text-green-500">
                      • Polling active
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 flex-wrap">
              <div className="hidden md:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Users className="w-4 h-4" />
                <span>{inbox.length} conversations</span>
                {notificationCount > 0 && (
                  <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                    {notificationCount} new
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-sm">
                  {isConnected ? (
                    <>
                      <Wifi className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">Connected</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-4 h-4 text-red-500" />
                      <span className="text-red-600">Disconnected</span>
                    </>
                  )}
                </div>
                
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
                    autoRefresh 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Zap className="w-3 h-3" />
                  {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
                </button>
                
                <button
                  onClick={handleManualRefresh}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
                    Total Chats
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalThreads}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
                    Active
                  </p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {stats.activeThreads}
                  </p>
                </div>
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
                    Resolved
                  </p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {stats.resolvedThreads || 0}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
                    Messages
                  </p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {stats.totalMessages}
                  </p>
                </div>
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
                    Unread
                  </p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {notificationCount}
                  </p>
                </div>
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Bell className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN LAYOUT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Conversations list */}
          <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col h-[calc(100vh-300px)] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="w-full pl-10 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </form>

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-3 h-3 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Filter:</span>
                </div>
                <span className="text-xs text-gray-500">
                  {filteredInbox.length} threads
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {['all', 'active', 'resolved', 'ended_by_user'].map((status) => {
                  const badge = getStatusBadge(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition flex items-center gap-1 ${
                        statusFilter === status
                          ? `${badge.class} border-transparent`
                          : 'bg-transparent text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span>{badge.icon}</span>
                      <span>{badge.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredInbox.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-gray-500 dark:text-gray-400">
                  <MessageCircle className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm font-medium">No conversations</p>
                  <p className="text-xs mt-1">
                    {statusFilter !== 'all' 
                      ? `No ${statusFilter} conversations` 
                      : 'No conversations found'}
                  </p>
                </div>
              ) : (
                filteredInbox.map((thread) => {
                  const badge = getStatusBadge(thread.status);
                  const isSelected = selectedThread?.thread_id === thread.thread_id;
                  
                  return (
                    <button
                      key={thread.thread_id}
                      onClick={() => loadThreadMessages(thread)}
                      className={`w-full text-left p-4 border-b border-gray-100 dark:border-gray-700 transition-all duration-200 group ${
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-900'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">
                            {thread.subject || 'Support Request'}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {thread.participantName || 'User'} • {thread.participantEmail || 'No email'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.class}`}>
                            {badge.icon} {badge.text}
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatDate(thread.updated_at)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <MessageCircle className="w-3 h-3" />
                          <span>{thread.messageCount || 0} messages</span>
                        </div>
                        {(thread.unreadCount > 0 || thread.isActiveRecently) && (
                          <div className="flex items-center gap-1">
                            {thread.unreadCount > 0 && (
                              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">
                                {thread.unreadCount} new
                              </span>
                            )}
                            {thread.isActiveRecently && (
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT: Active conversation */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col h-[calc(100vh-300px)] overflow-hidden">
            {!selectedThread ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Select a conversation
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                  Choose a chat from the left panel to view messages and reply to customers
                </p>
                <div className="mt-4 text-xs text-gray-500 space-y-1">
                  <p>• Inbox auto-refresh: 10 seconds</p>
                  <p>• Thread refresh: 8 seconds</p>
                  <p>• Heartbeat: 30 seconds</p>
                  <p>• Memory optimized polling</p>
                </div>
              </div>
            ) : (
              <>
                {/* THREAD HEADER */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {(selectedThread.participantName || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                            {selectedThread.participantName || 'User'}
                          </h2>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            selectedThread.status === 'active' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {selectedThread.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {selectedThread.participantEmail || 'No email provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* EXPORT BUTTON */}
                      <div className="relative">
                        <button
                          onClick={() => setShowExportMenu(!showExportMenu)}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Export
                        </button>
                        
                        {/* EXPORT DROPDOWN */}
                        {showExportMenu && (
                          <>
                            <div 
                              className="fixed inset-0 z-40"
                              onClick={() => setShowExportMenu(false)}
                            />
                            <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                              <div className="p-3">
                                <div className="flex justify-between items-center mb-2">
                                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Export Options
                                  </h4>
                                  <button
                                    onClick={() => setShowExportMenu(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                
                                <button
                                  onClick={handleExportText}
                                  disabled={exportLoading}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2 disabled:opacity-50"
                                >
                                  <Download className="w-4 h-4" />
                                  Download as Text File
                                </button>
                                
                                <button
                                  onClick={handleCopyToClipboard}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                                >
                                  <Copy className="w-4 h-4" />
                                  Copy to Clipboard
                                </button>
                                
                                <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                                
                                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email Recipient
                                  </label>
                                  <input
                                    type="email"
                                    value={exportRecipientEmail}
                                    onChange={(e) => setExportRecipientEmail(e.target.value)}
                                    placeholder="user@example.com"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-2"
                                  />
                                  
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Custom Message
                                  </label>
                                  <textarea
                                    value={exportMessage}
                                    onChange={(e) => setExportMessage(e.target.value)}
                                    placeholder="Optional message to include..."
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-2"
                                    rows="2"
                                  />
                                  
                                  <button
                                    onClick={handleEmailTranscript}
                                    disabled={exportLoading || !exportRecipientEmail}
                                    className="w-full px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
                                  >
                                    {exportLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                    Send Email
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* STATUS BUTTON */}
                      <select
                        value={selectedThread.status || 'active'}
                        onChange={(e) => handleUpdateStatus(e.target.value)}
                        className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Mark Active</option>
                        <option value="resolved">Mark Resolved</option>
                        <option value="closed">Mark Closed</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center justify-between">
                      <span>Created: {formatDate(selectedThread.created_at)}</span>
                      <span>Thread ID: {selectedThread.thread_id?.slice(0, 8)}...</span>
                    </div>
                  </div>
                </div>

                {/* MESSAGES */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 py-12">
                      <MessageCircle className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-sm font-medium">No messages yet</p>
                      <p className="text-xs mt-1">Start the conversation!</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const alignment = getMessageAlignment(message);
                        const senderName = getSenderName(message);
                        const showDate = index === 0 || 
                          new Date(message.created_at).toDateString() !== 
                          new Date(messages[index - 1].created_at).toDateString();

                        return (
                          <div key={`${message.message_id}_${index}`}>
                            {showDate && (
                              <div className="flex justify-center my-4">
                                <span className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                                  {formatDate(message.created_at)}
                                </span>
                              </div>
                            )}

                            <div className={`flex ${alignment.align === 'right' ? 'justify-end' : 'justify-start'} mb-2`}>
                              <div className={`max-w-[80%] md:max-w-xs ${
                                alignment.align === 'right' 
                                  ? 'order-2 ml-2' 
                                  : 'order-1 mr-2'
                              }`}>
                                <div className={`mb-1 ${alignment.align === 'right' ? 'text-right' : 'text-left'}`}>
                                  <span className={`text-xs font-semibold ${
                                    alignment.isAdmin ? 'text-red-600 dark:text-red-400' :
                                    alignment.isSupport ? 'text-blue-600 dark:text-blue-400' :
                                    'text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {senderName}
                                  </span>
                                </div>

                                <div className={`relative rounded-2xl px-4 py-3 shadow-sm ${
                                  alignment.align === 'right'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-br-none'
                                    : alignment.isAdmin
                                    ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-bl-none'
                                    : alignment.isSupport
                                    ? 'bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-bl-none'
                                    : message.sender_type === 'system'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-lg border border-yellow-200 dark:border-yellow-800'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'
                                }`}>
                                  <p className="text-sm break-words leading-relaxed">
                                    {message.content}
                                  </p>
                                  
                                  <div className={`text-[10px] mt-2 flex items-center gap-1 ${
                                    alignment.align === 'right'
                                      ? 'justify-end text-blue-100'
                                      : alignment.isAdmin || alignment.isSupport
                                      ? 'justify-start text-white/70'
                                      : message.sender_type === 'system'
                                      ? 'justify-start text-yellow-600 dark:text-yellow-400'
                                      : 'justify-start text-gray-500 dark:text-gray-400'
                                  }`}>
                                    <span>{formatTime(message.created_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* MESSAGE INPUT */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                  {error && (
                    <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="submit"
                      disabled={loading || !inputMessage.trim()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* SUCCESS MESSAGE */}
      {exportSuccess && (
        <div className="fixed bottom-4 right-4 px-4 py-3 bg-green-500 text-white rounded-lg shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span>Success! Check your email.</span>
        </div>
      )}
    </div>
  );
}